import { ReactElement } from "react";
import type { GetStaticProps } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiESQuery,
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { Heading } from "~/components/ui/Heading";
import { appConfig } from "~/config";
import { WhatsOnListing } from "~/components/content/WhatsOnListing";
import { PageSubMenu } from "~/components/ui/PageSubMenu";
import { FlexibleRow } from "~/components/flexibleContent/FlexibleRow";
import { sortEventsByDateAndTime } from "~/utils/eventsHelper";

const WhatsOnList = ({
  upcoming,
  current,
  currentEvents,
  currentPage,
}: {
  current: any;
  upcoming: any;
  currentPage: any;
  currentEvents: any;
}) => {
  const currentExhibitions = {
    atBrindleyplace: current?.data.filter(
      (exibition: any) =>
        exibition?.taxonomies?.programme_category.filter(
          (term: any) =>
            term.slug === "off-site" || term.slug === "international"
        )?.length === 0
    ),
    otherUk: current?.data
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "off-site"
          )?.length > 0
      )
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "international"
          )?.length === 0
      ),
    otherInternational: current?.data.filter(
      (exibition: any) =>
        exibition?.taxonomies?.programme_category.filter(
          (term: any) => term.slug === "international"
        )?.length > 0
    ),
    online: current?.data
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "online"
          )?.length > 0
      )
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) =>
              term.slug === "off-site" || term.slug === "international"
          )?.length === 0
      ),
  };

  const upcomingExhibitions = {
    atBrindleyplace: upcoming?.data.filter(
      (exibition: any) =>
        exibition?.taxonomies?.programme_category.filter(
          (term: any) =>
            term.slug === "off-site" || term.slug === "international"
        )?.length === 0
    ),
    otherUk: upcoming?.data
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "off-site"
          )?.length > 0
      )
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "international"
          )?.length === 0
      ),
    otherInternational: upcoming?.data.filter(
      (exibition: any) =>
        exibition?.taxonomies?.programme_category.filter(
          (term: any) => term.slug === "international"
        )?.length > 0
    ),
    online: upcoming?.data
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "online"
          )?.length > 0
      )
      .filter(
        (exibition: any) =>
          exibition?.taxonomies?.programme_category.filter(
            (term: any) =>
              term.slug === "off-site" || term.slug === "international"
          )?.length === 0
      ),
  };

  const events = {
    atBrindleyplace: sortEventsByDateAndTime(currentEvents?.data).filter(
      (event: any) =>
        event?.taxonomies?.programme_category.filter(
          (term: any) =>
            term.slug === "off-site" || term.slug === "international"
        )?.length === 0
    ),
    otherUk: sortEventsByDateAndTime(currentEvents?.data)
      .filter(
        (event: any) =>
          event?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "off-site"
          )?.length > 0
      )
      .filter(
        (event: any) =>
          event?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "international"
          )?.length === 0
      ),
    otherInternational: sortEventsByDateAndTime(currentEvents?.data).filter(
      (event: any) =>
        event?.taxonomies?.programme_category.filter(
          (term: any) => term.slug === "international"
        )?.length > 0
    ),
    online: sortEventsByDateAndTime(currentEvents?.data)
      .filter(
        (event: any) =>
          event?.taxonomies?.programme_category.filter(
            (term: any) => term.slug === "online"
          )?.length > 0
      )
      .filter(
        (event: any) =>
          event?.taxonomies?.programme_category.filter(
            (term: any) =>
              term.slug === "off-site" || term.slug === "international"
          )?.length === 0
      ),
  };

  return (
    <>
      <NextHeadSeo
        canonical={currentPage?.yoast_head_json?.canonical}
        title={currentPage?.yoast_head_json?.title ?? currentPage?.title}
        description={currentPage?.yoast_head_json?.description}
        og={{
          title: currentPage?.yoast_head_json?.og_title,
          type: currentPage?.yoast_head_json?.og_type,
          siteName: currentPage?.yoast_head_json?.og_site_name,
          image: currentPage?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header>
        <PageSubMenu
          title={currentPage?.title}
          menu={currentPage?.subMenu?.menu}
        />
      </Header>
      <main id="content">
        {currentPage?.acf?.content?.length > 0 && (
          <FlexibleRow field="content" content={currentPage.acf.content} />
        )}

        {current?.data?.length > 0 && (
          <PageMargins
            spaceTop={!currentPage?.acf?.content?.length ? 6 : undefined}
          >
            <Heading heading="h1" space={10}>
              CURRENT EXHIBITIONS
            </Heading>
          </PageMargins>
        )}
        {currentExhibitions.atBrindleyplace?.length > 0 && (
          <WhatsOnListing
            keyPrefix="current"
            items={currentExhibitions.atBrindleyplace}
            spaceBottom={
              currentExhibitions.otherUk?.length > 0 ||
              currentExhibitions.otherInternational?.length > 0 ||
              currentExhibitions.online?.length > 0
                ? 6
                : undefined
            }
            heading={
              <PageMargins
                spaceTop={!currentPage?.acf?.content?.length ? 6 : undefined}
              >
                <Heading heading="h3" asTag space={8}>
                  Ikon Gallery, Brindleyplace
                </Heading>
              </PageMargins>
            }
          />
        )}

        {currentExhibitions.otherUk?.length > 0 && (
          <WhatsOnListing
            keyPrefix="current"
            items={currentExhibitions.otherUk}
            spaceBottom={
              currentExhibitions.otherInternational?.length > 0 ||
              currentExhibitions.online?.length > 0
                ? 6
                : undefined
            }
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !currentExhibitions.atBrindleyplace?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Off-site, UK
                </Heading>
              </PageMargins>
            }
          />
        )}

        {currentExhibitions.otherInternational?.length > 0 && (
          <WhatsOnListing
            keyPrefix="current"
            items={currentExhibitions.otherInternational}
            spaceBottom={currentExhibitions.online?.length > 0 ? 6 : undefined}
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !currentExhibitions.atBrindleyplace?.length &&
                  !currentExhibitions.otherUk?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Off-site, International
                </Heading>
              </PageMargins>
            }
          />
        )}

        {currentExhibitions.online?.length > 0 && (
          <WhatsOnListing
            keyPrefix="current"
            items={currentExhibitions.online}
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !currentExhibitions.atBrindleyplace?.length &&
                  !currentExhibitions.otherUk?.length &&
                  !currentExhibitions.otherInternational?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Online
                </Heading>
              </PageMargins>
            }
          />
        )}

        {upcoming?.data?.length > 0 && (
          <PageMargins
            spaceTop={
              !currentPage?.acf?.content?.length && !current?.data?.length
                ? 6
                : undefined
            }
          >
            <Heading heading="h1" space={10}>
              UPCOMING EXHIBITIONS
            </Heading>
          </PageMargins>
        )}
        {upcomingExhibitions.atBrindleyplace?.length > 0 && (
          <WhatsOnListing
            keyPrefix="upcoming"
            items={upcomingExhibitions.atBrindleyplace}
            spaceBottom={
              upcomingExhibitions.otherUk?.length > 0 ||
              upcomingExhibitions.otherInternational?.length > 0 ||
              upcomingExhibitions.online?.length > 0
                ? 6
                : undefined
            }
            heading={
              <PageMargins
                spaceTop={!currentPage?.acf?.content?.length ? 6 : undefined}
              >
                <Heading heading="h3" asTag space={8}>
                  Ikon Gallery, Brindleyplace
                </Heading>
              </PageMargins>
            }
          />
        )}

        {upcomingExhibitions.otherUk?.length > 0 && (
          <WhatsOnListing
            keyPrefix="upcoming"
            items={upcomingExhibitions.otherUk}
            spaceBottom={
              upcomingExhibitions.otherInternational?.length > 0 ||
              upcomingExhibitions.online?.length > 0
                ? 6
                : undefined
            }
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !upcomingExhibitions.atBrindleyplace?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Off-site, UK
                </Heading>
              </PageMargins>
            }
          />
        )}

        {upcomingExhibitions.otherInternational?.length > 0 && (
          <WhatsOnListing
            keyPrefix="upcoming"
            items={upcomingExhibitions.otherInternational}
            spaceBottom={upcomingExhibitions.online?.length > 0 ? 6 : undefined}
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !upcomingExhibitions.atBrindleyplace?.length &&
                  !upcomingExhibitions.otherUk?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Off-site, International
                </Heading>
              </PageMargins>
            }
          />
        )}

        {upcomingExhibitions.online?.length > 0 && (
          <WhatsOnListing
            keyPrefix="upcoming"
            items={upcomingExhibitions.online}
            heading={
              <PageMargins
                spaceTop={
                  !currentPage?.acf?.content?.length &&
                  !upcomingExhibitions.atBrindleyplace?.length &&
                  !upcomingExhibitions.otherUk?.length &&
                  !upcomingExhibitions.otherInternational?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Online
                </Heading>
              </PageMargins>
            }
          />
        )}

        {currentEvents?.data?.length > 0 && (
          <>
            <PageMargins
              spaceTop={
                !currentPage?.acf?.content?.length &&
                !current?.data?.length &&
                !upcoming?.data?.length
                  ? 6
                  : undefined
              }
            >
              <Heading heading="h1" space={10}>
                EVENTS
              </Heading>
            </PageMargins>

            {events.atBrindleyplace?.length > 0 && (
              <WhatsOnListing
                keyPrefix="events"
                items={events.atBrindleyplace}
                spaceBottom={
                  events.otherUk?.length > 0 ||
                  events.otherInternational?.length > 0 ||
                  events.online?.length > 0
                    ? 6
                    : undefined
                }
                heading={
                  <PageMargins
                    spaceTop={
                      !currentPage?.acf?.content?.length ? 6 : undefined
                    }
                  >
                    <Heading heading="h3" asTag space={8}>
                      Ikon Gallery, Brindleyplace
                    </Heading>
                  </PageMargins>
                }
              />
            )}

            {events.otherUk?.length > 0 && (
              <WhatsOnListing
                keyPrefix="events"
                items={events.otherUk}
                spaceBottom={
                  events.otherInternational?.length > 0 ||
                  events.online?.length > 0
                    ? 6
                    : undefined
                }
                heading={
                  <PageMargins
                    spaceTop={
                      !currentPage?.acf?.content?.length &&
                      !events.atBrindleyplace?.length
                        ? 6
                        : undefined
                    }
                  >
                    <Heading heading="h3" asTag space={8}>
                      Off-site, UK
                    </Heading>
                  </PageMargins>
                }
              />
            )}

            {events.otherInternational?.length > 0 && (
              <WhatsOnListing
                keyPrefix="events"
                items={events.otherInternational}
                spaceBottom={events.online?.length > 0 ? 6 : undefined}
                heading={
                  <PageMargins
                    spaceTop={
                      !currentPage?.acf?.content?.length &&
                      !events.atBrindleyplace?.length &&
                      !events.otherUk?.length
                        ? 6
                        : undefined
                    }
                  >
                    <Heading heading="h3" asTag space={8}>
                      Off-site, International
                    </Heading>
                  </PageMargins>
                }
              />
            )}

            {events.online?.length > 0 && (
              <WhatsOnListing
                keyPrefix="events"
                items={events.online}
                heading={
                  <PageMargins
                    spaceTop={
                      !currentPage?.acf?.content?.length &&
                      !events.atBrindleyplace?.length &&
                      !events.otherUk?.length &&
                      !events.otherInternational?.length
                        ? 6
                        : undefined
                    }
                  >
                    <Heading heading="h3" asTag space={8}>
                      Online
                    </Heading>
                  </PageMargins>
                }
              />
            )}
          </>
        )}
      </main>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const currentPage = await restApiGetPostBySlugOrFallbackId(
    "page",
    "whats-on",
    token
  );

  let today = new Date().toISOString().split("T")?.[0];

  const upcoming = await restApiESQuery({
    type: "exhibition",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateBegin: today,
    dateBeginCompare: "gt",
  });

  const current = await restApiESQuery({
    type: "exhibition",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateBegin: today,
    dateBeginCompare: "lte",
    dateEnd: today,
    dateEndCompare: "gte",
  });

  const currentEvents = await restApiESQuery({
    type: "event",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateEnd: today,
    dateEndCompare: "gte",
  });

  const frontendSettings = await restApiESGetSettings(["programmeCategory"]);
  if (!upcoming?.data && !current?.data && !currentEvents?.data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      upcoming,
      current,
      currentEvents,
      currentPage,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("exhibitionListing")
      : 15,
  };
};

WhatsOnList.getLayout = function getLayout(page: ReactElement) {
  return <LayoutMain>{page}</LayoutMain>;
};
export default WhatsOnList;
